import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { billingService, Plan } from '@common/api/billingService';
import { addQueryParams } from '@common/utils/url';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ContactSalesModal from './ContactSalesModal';
import PricingCard, { PlanData } from './PricingCard';
import styles from './PricingModal.module.scss';

interface PricingModalProps {
    isOpen: boolean;
    onClose: () => void;
    fisrt?: boolean;
}

export const PricingModal: React.FC<PricingModalProps> = ({
    isOpen, onClose, fisrt 
}) => {
    const {
        openSignInModal, user, userBilling: { userPlan, planText } 
    } = useAuth();
    const { showToast } = useToast();
    const [isContactSalesOpen, setIsContactSalesOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('yearly');

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await billingService.getPlans();
                if (response) {
                    setPlans(response);
                }
            } catch (err) {
                console.error('Failed to fetch plans', err);
            }
        };
        fetchPlans();
    }, []);

    if (!isOpen) return null;

    const handleCreateSubscription = async () => {
        if (!user) {
            openSignInModal();
            return;
        }
        if (fisrt) {
            onClose();
            return;
        }

        setLoading(true);

        try {
            const plan = plans.find(plan => plan.code === 'BASIC' && plan.billingCycle === billingCycle);
            if (!plan) {
                console.error('No plan found');
                showToast('Basic plan not found', 'error');
                return;
            }

            const successUrl = addQueryParams(window.location.origin + window.location.pathname, {
                pay_result: 'success', planCode: plan.code, planName: plan.name
            });
            const cancelUrl = addQueryParams(window.location.origin + window.location.pathname, { pay_result: 'cancel' });

            const response = await billingService.createSubscription({
                planId: plan.id,
                successUrl,
                cancelUrl
            });
            if (response.success) {
                const isShopify = window.top !== window.self;
                if (isShopify) {
                    window.open(response.paymentUrl, '_top');
                } else {
                    window.location.href = response.paymentUrl;
                }
            }
        } catch (err) {
            showToast('Failed to create subscription', 'error');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        try {
            const response = await billingService.createSubscriptionPortal(window.location.href);
            if (response.paymentType === 'shopify') {
                window.location.reload();
                return;
            }
            if (response.url) {
                window.location.href = response.url;
            } else {
                showToast('Failed to cancel subscription', 'error');
            }
        } catch (error) {
            showToast('Failed to cancel subscription', 'error');
            console.log('error', error);
        }
    }

    const isCanceled = userPlan?.status === 'pending_cancellation';
    const userBillingCycle = userPlan?.billingCycle as 'monthly' | 'yearly' | undefined;

    const basicPlan = plans.find(p => p.code === 'BASIC' && p.billingCycle === billingCycle);
    const basicPrice = basicPlan
        ? (billingCycle === 'yearly' ? Math.round(basicPlan.priceCents / 12) : basicPlan.priceCents)
        : (billingCycle === 'monthly' ? 699 : 559);

    const uiPlans: PlanData[] = [
        {
            name: 'Basic',
            price: basicPrice,
            description: 'For small businesses and startups',
            buttonText: 'Upgrade',
            isPopular: true,
            features: [
                'AI Dashboard — Personalized to each role',
                'Daily Pulse — AI insights every morning',
                'Ask Bustly AI — Ask anything, anytime',
                'Integrations — Commerce and ads tools',
                'Email support'
            ]
        },
        {
            name: 'Enterprise',
            price: 'Custom',
            description: 'For large teams & multi-brand portfolios',
            buttonText: 'Contact Sales',
            isPopular: false,
            isCustom: true,
            features: [
                'Everything in Basic, plus',
                '50+ integrations — All your tools connected',
                'Custom AI models — Trained on your business',
                'Dedicated success manager',
                'Enterprise SSO/SAML'
            ]
        }
    ];

    return createPortal(
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.centeredWrapper}>
                <div 
                    className={styles.modalContainer}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button 
                        onClick={onClose}
                        className={styles.closeButton}
                    >
                        <X size={24} />
                    </button>

                    <div className={styles.header}>
                        <h2 className={styles.title}>Pricing for Bustly</h2>
                        
                        <div className={styles.billingToggle}>
                            <button
                                type="button"
                                onClick={() => setBillingCycle('monthly')}
                                className={`${styles.billingButton} ${billingCycle === 'monthly' ? styles.active : ''}`}
                            >
                                Monthly
                            </button>
                            <button
                                type="button"
                                onClick={() => setBillingCycle('yearly')}
                                className={`${styles.billingButton} ${billingCycle === 'yearly' ? styles.active : ''}`}
                            >
                                Yearly
                                <span className={styles.saveBadge}>
                                    Save 20%
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className={styles.cardsContainer}>
                        <div className={styles.grid}>
                            {uiPlans.map((plan, index) => {
                                let buttonText = plan.buttonText;
                                let isDisabled = false;

                                if (plan.name === 'Basic' && userPlan?.planCode === 'BASIC') {
                                    if (userBillingCycle === billingCycle) {
                                        buttonText = 'Current Plan';
                                        isDisabled = true;
                                    } else if (userBillingCycle === 'monthly' && billingCycle === 'yearly') {
                                        buttonText = 'Switch to Yearly';
                                    } else if (userBillingCycle === 'yearly' && billingCycle === 'monthly') {
                                        buttonText = 'Switch to Monthly';
                                    }
                                }

                                return (
                                    <PricingCard
                                        key={index}
                                        plan={{...plan, buttonText}}
                                        loading={plan.name === 'Basic' && loading}
                                        disabled={isDisabled}
                                        onAction={() => {
                                            if (plan.buttonText === 'Contact Sales') {
                                                setIsContactSalesOpen(true);
                                            } else {
                                                handleCreateSubscription();
                                            }
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {userPlan?.planCode === 'BASIC' && (
                        <div className={styles.cancelSubscription} onClick={handleCancelSubscription}>
                            {window.isShopify ? 'Cancel subscription' : 'Edit billing'}
                        </div>
                    )}
                </div>
            </div>
            
            <ContactSalesModal 
                isOpen={isContactSalesOpen} 
                onClose={() => setIsContactSalesOpen(false)} 
            />
        </div>,
        document.body
    );
};

export * from './Welcome';
